// ConversationsPage — /hub/conversations (Epic C1).
//
// Three objects, not one list: channels & DMs are places; threads are a queue.
// The page owns the fetch state per section (channels / DMs / threads each have
// their own loading | ready | error), so the rail fills in progressively instead
// of blocking on the slowest fetch.
//
// LOAD ORDER is sequential, not parallel: rooms (channels + DMs) first, then
// threads — the user's explicit ask ("load channels first, then DMs, then
// threads"). Channels and DMs arrive together in one `fn_conversation_rooms` RPC
// (they cannot be split at the network level), so they share a load but render in
// order — channels above DMs. Threads come from a separate `fn_conversation_threads`
// RPC and only start AFTER rooms settles (success or error), so threads can never
// appear before channels. A rooms failure still kicks off threads (a rooms error
// must not starve threads), and a threads failure never blanks the channels.
//
// fn_conversation_rooms has a transient 500 on the cold first call (Postgres cold
// cache / 57014 statement timeout); convRpc retries 5xx with backoff, so the user
// never sees that 500 as an error.
//
// The rail is presentational; the reading pane (Epic C2) renders the selected
// channel/DM/thread via ChannelStream / DmStream / ThreadView.
//
// Asymmetric split (§5 rule 3): the rail is narrow (md:w-80), the reading pane is
// much wider. A 50/50 split would give a list of channel names the same weight as
// five years of client conversation. Mobile: the rail is a sheet; selecting a
// conversation fills the screen.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { HubShell } from "../../components/hub/shell";
import { EmptyState } from "../../components/shared/StateMessage";
import PanelErrorBoundary from "../../components/shared/PanelErrorBoundary";
import ConversationsRail from "../../components/hub/conversations/ConversationsRail";
import { ConversationsRailSkeleton, ConversationsReadingSkeleton } from "../../components/hub/conversations/ConversationsSkeleton";
import ChannelStream from "../../components/hub/conversations/ChannelStream";
import DmStream from "../../components/hub/conversations/DmStream";
import ThreadView from "../../components/hub/conversations/ThreadView";
import {
  getConversationRooms,
  getConversationThreads,
  getConversationCounts,
  getRoutingRules,
} from "../../api/conversations";

// Poll the open-threads count this often (matches the sidebar badge) so the
// page header and Threads-section label stay as fresh as the badge.
const POLL_MS = 60000;

// Map a threads sub-lens to the getConversationThreads params.
// 'awaiting' reuses scope:'open' and filters client-side — there is no confirmed
// server scope for awaiting_us; the flag is on each item (UNCONFIRMED shape).
const lensToParams = (lens) => {
  if (lens === "design") return { scope: "open", channel: "design_review" };
  return { scope: "open" };
};
const filterByLens = (items, lens) =>
  lens === "awaiting" ? items.filter((t) => t && t.awaiting_us) : items;

export default function ConversationsPage() {
  // Per-section fetch state. Each resolves independently so the rail reveals
  // channels → DMs → threads as data lands, not all-at-once.
  const [channelsStatus, setChannelsStatus] = useState("loading");
  const [dmsStatus, setDmsStatus] = useState("loading");
  const [threadsStatus, setThreadsStatus] = useState("loading");
  const [rooms, setRooms] = useState(null);
  const [threads, setThreads] = useState(null);
  // Polled open-threads count (fn_conversation_counts) — keeps the page header
  // and the Threads-section label in step with the sidebar badge. The counts
  // effect fires on mount AND every POLL_MS, so this is the canonical source
  // (perf memo §4.2). It must NOT fall back to rooms.counts.open_threads — that
  // block is the old unscoped one. null here only means "first poll hasn't
  // landed yet", and the header hides the number rather than show a stale one.
  const [openThreads, setOpenThreads] = useState(null);
  // Per-lens totals for the three Threads sub-tabs (Open / Awaiting us / Design).
  // `open` comes from the polled openCount below; `design` is a one-shot
  // design_review-scoped fetch (fires once, after rooms resolves); `awaiting`
  // has no server scope — it is derived from the loaded open-scope items
  // (awaiting_us === true) in loadThreads. null = "not known yet" → no count
  // shown for that tab until the value lands.
  const [lensCounts, setLensCounts] = useState({ awaiting: null, design: null });
  const [threadLens, setThreadLens] = useState("open");
  const [showQuiet, setShowQuiet] = useState(false);
  const [before, setBefore] = useState(null); // next_before cursor for "Load more"
  // `?thread=<id>` deep-links a thread (the Now promise panel's "Open conversation").
  const [searchParams, setSearchParams] = useSearchParams();
  const threadParam = searchParams.get("thread");
  const [selection, setSelection] = useState(() =>
    threadParam ? { type: "thread", id: threadParam } : null,
  );
  useEffect(() => {
    if (threadParam) setSelection({ type: "thread", id: threadParam });
  }, [threadParam]);

  // Epic 2 — the set of `term`s that already have a routing rule (ops_routing_rules).
  // Fetched once per page session (it changes rarely). null = not loaded yet (the
  // composer treats that as "no rules known" so Accept stays visible — hiding is a
  // nicety, not a gate). A failure logs and leaves the set empty; the DB function
  // still refuses a true duplicate, so a stale/missing set cannot cause one.
  const [routingTerms, setRoutingTerms] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const terms = await getRoutingRules();
        if (!cancelled) setRoutingTerms(terms);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[ConversationsPage] routing rules", err);
        if (!cancelled) setRoutingTerms(new Set());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Optimistic: when a rule is accepted, add the term to the session set so the
  // Accept button stays hidden for that term across threads (no re-fetch needed).
  const onRoutingRuleAccepted = useCallback((term) => {
    if (!term) return;
    setRoutingTerms((prev) => {
      const next = prev ? new Set(prev) : new Set();
      next.add(term);
      return next;
    });
  }, []);

  // Channels + DMs come from one rooms RPC (fn_conversation_rooms). Reveal
  // channels first, then DMs — same payload, but channels render above DMs in
  // the rail. This MUST resolve before threads starts (see the sequential effect
  // below). convRpc retries the transient 500 on the cold first call (with
  // backoff), so this rarely surfaces as an error.
  const loadRooms = useCallback(async () => {
    setChannelsStatus("loading");
    setDmsStatus("loading");
    try {
      const roomsRes = await getConversationRooms({ includeQuiet: showQuiet });
      setRooms(roomsRes || null);
      setChannelsStatus("ready");
      setDmsStatus("ready");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[ConversationsPage] rooms", err);
      setRooms(null);
      setChannelsStatus("error");
      setDmsStatus("error");
    }
  }, [showQuiet]);

  // Threads load AFTER rooms resolves (sequential, see the effect below). A
  // threads failure or slowness never gates the channels/DMs above. `lens` is
  // passed explicitly so lens changes don't have to wait on a state update.
  const fetchThreads = useCallback(async (lens, cursor = null) => {
    const res = await getConversationThreads({
      ...lensToParams(lens),
      limit: 100,
      before: cursor,
    });
    const base = res || {};
    return {
      ...base,
      items: filterByLens(Array.isArray(base.items) ? base.items : [], lens),
    };
  }, []);

  const loadThreads = useCallback(
    async (lens = "open") => {
      setThreadsStatus("loading");
      try {
        const fresh = await fetchThreads(lens);
        setThreads(fresh);
        setBefore(fresh.next_before || null);
        setThreadsStatus("ready");
        // "Awaiting us" has no server scope (awaiting_us is a per-item flag, and
        // the lens filters client-side — see lensToParams / filterByLens). Derive
        // its tab count from the open-scope items, which only the open lens
        // carries unfiltered. This is a loaded-page count until the backend
        // exposes a counts.awaiting / an awaiting scope; it matches how the
        // Awaiting-us lens itself already works (it only ever shows loaded items).
        if (lens === "open") {
          const awaiting = (Array.isArray(fresh.items) ? fresh.items : [])
            .filter((t) => t && t.awaiting_us).length;
          setLensCounts((prev) =>
            prev.awaiting === awaiting ? prev : { ...prev, awaiting },
          );
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[ConversationsPage] threads", err);
        setThreads(null);
        setThreadsStatus("error");
      }
    },
    [fetchThreads],
  );

  // One-shot fetch of the Design tab count: fn_conversation_threads scoped to
  // the design_review channel returns counts.open as the design total. Fired
  // once, AFTER rooms resolves (same sequential gate as loadThreads), so it
  // never starts before channels/DMs — preserving the load-order contract. A
  // failure logs and leaves design null (no count shown) rather than blocking.
  const loadLensCounts = useCallback(async () => {
    try {
      const res = await getConversationThreads({
        scope: "open",
        channel: "design_review",
        limit: 1,
      });
      const design =
        res && res.counts && typeof res.counts.open === "number"
          ? res.counts.open
          : null;
      setLensCounts((prev) =>
        prev.design === design ? prev : { ...prev, design },
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[ConversationsPage] design lens count", err);
    }
  }, []);

  // SEQUENTIAL LOAD — rooms first, then threads. Not parallel.
  //
  // The user's explicit ask: "load channels first, then DMs, then threads." Since
  // channels + DMs arrive in one fn_conversation_rooms RPC, the practical order is:
  //   1. rooms (channels + DMs)   ← shown first
  //   2. threads                  ← shown after rooms resolves
  //
  // Firing both in parallel let threads appear BEFORE channels when the rooms RPC
  // hit a transient 500 (cold Postgres cache) — threads succeeded while channels
  // errored, so threads was "the first loading." Chaining rooms → threads fixes
  // that: channels+DMs are always attempted first, and threads only starts after
  // rooms settles (success OR error — a rooms failure must not starve threads).
  //
  // On a quiet-toggle re-fetch, only rooms re-loads (threads are unaffected by the
  // quiet filter). The roomsLoadedOnce ref gates the threads kick-off to the first
  // rooms resolution only.
  const roomsLoadedOnce = useRef(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await loadRooms();
      if (cancelled) return;
      if (!roomsLoadedOnce.current) {
        roomsLoadedOnce.current = true;
        loadThreads();
        loadLensCounts();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadRooms, loadThreads, loadLensCounts]);

  // Keep the open-threads count fresh. The sidebar badge polls
  // fn_conversation_counts every POLL_MS; without this, the page header and the
  // Threads-section label (both read rooms.counts.open_threads, fetched once on
  // mount) freeze at the page-load value while the badge stays fresh — so the
  // three numbers drift apart (the threads-section-vs-badge mismatch). This polls
  // the lightweight counts RPC (NOT the 57014-prone fn_conversation_rooms scan)
  // so the page stays in step with the badge without re-running the heavy scan.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const c = await getConversationCounts();
        if (!cancelled && c && typeof c.open_threads === "number") {
          setOpenThreads(c.open_threads);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[ConversationsPage] counts", err);
      }
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  // Re-fetch threads only (lighter) when the sub-lens changes — rooms are stable
  // across lens changes, so we don't tear down channels/DMs. Routes through
  // loadThreads so the threads section flips to its own loader, not the rail's.
  const onThreadLensChange = useCallback(
    async (lens) => {
      setThreadLens(lens);
      setBefore(null);
      await loadThreads(lens);
    },
    [loadThreads],
  );

  // Load-more appends; it does NOT reset threadsStatus (no reload flash on a
  // successful append). A failure leaves the existing items in place rather than
  // blanking the section — the user keeps what they already loaded.
  const onLoadMore = useCallback(async () => {
    if (!before) return;
    try {
      const more = await fetchThreads(threadLens, before);
      setThreads((prev) => ({
        ...more,
        items: [...(prev?.items || []), ...more.items],
      }));
      setBefore(more.next_before || null);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[ConversationsPage] threads load-more", err);
    }
  }, [before, threadLens, fetchThreads]);

  const onToggleQuiet = useCallback(() => {
    setShowQuiet((q) => !q); // loadRooms re-runs via the effect when showQuiet changes.
  }, []);

  const onSelect = useCallback(
    (type, id) => {
      setSelection({ type, id });
      // Picking from the rail replaces the deep link, so a refresh doesn't snap back to it.
      if (threadParam) {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete("thread");
            return next;
          },
          { replace: true },
        );
      }
    },
    [threadParam, setSearchParams],
  );

  // Resolve the selected channel / DM / thread from the loaded rail data, so the
  // reading pane (C2) receives the full row (client_id, client_facing, name/with,
  // subject, summary, …). The rail's onSelect passes type + id; the pane finds the
  // row by id. A selection that is no longer in the data (e.g. filtered out by a
  // lens change) falls through to the empty state.
  const selectedChannel = useMemo(
    () =>
      selection?.type === "channel"
        ? (rooms?.channels || []).find((c) => c.id === selection.id) || null
        : null,
    [selection, rooms],
  );
  const selectedDm = useMemo(
    () =>
      selection?.type === "dm"
        ? (rooms?.dms || []).find((d) => d.id === selection.id) || null
        : null,
    [selection, rooms],
  );
  // A deep-linked thread that is not in the loaded open-scope page (an old or resolved
  // one) falls back to `{ id }`: ThreadView loads the conversation by id and only
  // optionally reads `assigned` / `assigned_to`. Any other missing selection keeps the
  // "no longer visible" state.
  const selectedThread = useMemo(() => {
    if (selection?.type !== "thread") return null;
    const listed = (threads?.items || []).find((t) => t.id === selection.id);
    if (listed) return listed;
    return selection.id === threadParam ? { id: selection.id } : null;
  }, [selection, threads, threadParam]);

  // C3 — sync the rail when a thread is resolved or assigned from the reading
  // pane, so the row reflects the new state immediately (no full refetch).
  const onThreadResolved = useCallback((threadId, nextResolved) => {
    setThreads((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        items: (prev.items || []).map((it) =>
          it.id === threadId ? { ...it, resolved: nextResolved } : it,
        ),
      };
    });
  }, []);

  const onThreadAssigned = useCallback((threadId, member) => {
    setThreads((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        items: (prev.items || []).map((it) =>
          it.id === threadId
            ? { ...it, assigned_to: member.id, assigned: member.name }
            : it,
        ),
      };
    });
  }, []);


  // Canonical open-threads count comes from fn_conversation_counts (polled on
  // mount + every POLL_MS, in step with the sidebar badge and deduped in-flight
  // with it). Do NOT fall back to rooms.counts.open_threads — that block is the
  // old unscoped one (perf memo §4.2). null only until the first counts
  // response lands, then the header shows the live number.
  const openCount = openThreads;

  // The whole-rail skeleton shows only before the FIRST section has resolved —
  // after that the real rail renders and each section carries its own loader.
  const railInitialLoading =
    channelsStatus === "loading" &&
    dmsStatus === "loading" &&
    threadsStatus === "loading";
  // The reading pane skeleton shows only during the very first load; once any
  // section is ready the user can pick something to read.
  const initialLoading = railInitialLoading;

  return (
    <HubShell>
      <div className="flex h-full flex-col">
        <header className="flex items-center gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-800 md:px-6 lg:px-10">
          <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Conversations
          </h1>
          {channelsStatus === "ready" && typeof openCount === "number" && (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {openCount} open
            </span>
          )}
        </header>

        <div className="flex min-h-0 flex-1 flex-col md:flex-row">
          {/* Rail — narrow. A sheet on mobile (full width); fixed width on md+. */}
          <aside
            className="h-64 shrink-0 border-b border-gray-200 dark:border-gray-800 md:h-auto md:w-80 md:border-b-0 md:border-r"
            data-testid="conversations-rail-pane"
          >
            {railInitialLoading ? (
              <ConversationsRailSkeleton />
            ) : (
              <ConversationsRail
                rooms={rooms}
                openThreadsCount={openCount}
                threads={threads}
                threadLensCounts={{
                  open: typeof openCount === "number" ? openCount : null,
                  awaiting: lensCounts.awaiting,
                  design: lensCounts.design,
                }}
                threadLens={threadLens}
                onThreadLensChange={onThreadLensChange}
                showQuiet={showQuiet}
                onToggleQuiet={onToggleQuiet}
                onLoadMoreThreads={onLoadMore}
                selection={selection}
                onSelect={onSelect}
                channelsStatus={channelsStatus}
                dmsStatus={dmsStatus}
                threadsStatus={threadsStatus}
                onRetryRooms={() => loadRooms()}
                onRetryThreads={() => loadThreads(threadLens)}
              />
            )}
          </aside>

          {/* Reading pane — much wider (the asymmetric split). C2 wires the panes. */}
          <main
            className="min-w-0 flex-1 overflow-y-auto"
            data-testid="conversations-reading-pane"
          >
            <PanelErrorBoundary label="conversations-reading-pane">
              {initialLoading ? (
                <ConversationsReadingSkeleton />
              ) : !selection ? (
                <div className="p-6">
                  <EmptyState
                    variant="inset"
                    message="Select a channel, DM, or thread to read it."
                    padding="py-16"
                  />
                </div>
              ) : selection.type === "channel" && selectedChannel ? (
                <ChannelStream channel={selectedChannel} />
              ) : selection.type === "dm" && selectedDm ? (
                <DmStream dm={selectedDm} />
              ) : selection.type === "thread" && selectedThread ? (
                <ThreadView
                  thread={selectedThread}
                  onResolved={onThreadResolved}
                  onAssigned={onThreadAssigned}
                  existingTerms={routingTerms}
                  onRoutingRuleAccepted={onRoutingRuleAccepted}
                />
              ) : (
                <div className="p-6">
                  <EmptyState
                    variant="inset"
                    message="This conversation is no longer visible. Select another from the rail."
                    padding="py-16"
                  />
                </div>
              )}
            </PanelErrorBoundary>
          </main>
        </div>
      </div>
    </HubShell>
  );
}

